import useAuth from "containers/auth/useHooks";
import useStore from "hooks/useStore";
import { ATTENDEE_APIS } from "libs/apis/attendee.api";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { successToaster } from "utils/helpers/common/alert-service";
import { siteRoutes } from "utils/helpers/enums/routes";
import { AddMinorToEventDTO } from "utils/helpers/models/attendee/add-minor-to-event.dto";
import { CompleteAttendeeProfileDTO } from "utils/helpers/models/attendee/complete-profile.dto";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import { ReviewFilterDTO } from "utils/helpers/models/attendee/review-filter-dto";
import { ChangePasswordDTO } from "utils/helpers/models/shared/change-password.dto";
import { CommunityDTO } from "utils/helpers/models/shared/community.dto";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import { FilterDTO } from "utils/helpers/models/shared/filter.dto";
import { MessageDTO } from "utils/helpers/models/shared/message.dto";
import { PictureDTO } from "utils/helpers/models/shared/picture.dto";
import { UserDTO } from "utils/helpers/models/shared/user.dto";

const useAttendee = () => {
  const navigate = useNavigate();
  const { getUserProfile } = useAuth();
  const { getUserId, updateProfileCompletionStatus } = useStore();
  const [minor, setMinor] = useState<MinorDTO>(new MinorDTO());
  const { setAuthData } = useAuth();
  const updateProfile = async (body: CompleteAttendeeProfileDTO) => {
    const response = await ATTENDEE_APIS.updateProfile(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      setAuthData(response);
      navigate(siteRoutes.declarationForm);
    }
  };

  const EditProfileAttendee = async (body: CompleteAttendeeProfileDTO) => {
    const response = await ATTENDEE_APIS.updateProfile(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      setAuthData(response);
      navigate(siteRoutes.attendeeHome);
    }
  };

  const getAttendeeMinors = async (
    setData: Function,
    queryParams: any = {},
    previousData: MinorDTO[] = [],
    setFilters?: Function
  ) => {
    const response = await ATTENDEE_APIS.getAttendeeMinors(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages, totalElements } = data;
      if (setFilters)
        setFilters((filters) => ({ ...filters, totalPages, totalElements }));
      const events = data.content.map((i) => new MinorDTO(i));
      setData(Array.isArray(data.content) ? [...previousData, ...events] : []);
    }
  };

  const addMinorToProfile = async (body: MinorDTO) => {
    const response = await ATTENDEE_APIS.addMinorToProfile([body]);
    const { status = false, message = "" } = response || {};
    if (status) {
      getUserProfile();
      successToaster(message);
      navigate(-1);
    }
  };

  const getAttendeeHome = async (
    setUpcomingEvents: Function,
    setRecommendedEvents: Function
  ) => {
    const response = await ATTENDEE_APIS.getAttendeeHome();
    const { status = false, data = {} } = response || {};
    if (status) {
      const { upcomingEvents = [], recommendedEvents = [] } = data;
      setUpcomingEvents(upcomingEvents.map((i) => new EventDTO(i)));
      setRecommendedEvents(recommendedEvents.map((i) => new EventDTO(i)));
    }
  };

  const getUserById = async (userId: number, setData: Function) => {
    const response = await ATTENDEE_APIS.getUserById(userId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const addMinorToEvent = async (eventId: number, body: AddMinorToEventDTO) => {
    const response = await ATTENDEE_APIS.addMinorToEvent(eventId, body);
    const { status = false, message = "", data = {} } = response || {};
    if (status) {
      const { checkoutUrl } = data;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        successToaster(message);
        navigate(-1);
      }
    }
  };

  const addToWishlist = async (eventId: number) => {
    const response = await ATTENDEE_APIS.addToWishlist(eventId);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getWishlistedMinors = async (eventId: number, setData: Function) => {
    const response = await ATTENDEE_APIS.getWishlistedMinors(eventId);
    const { status = false, data = [] } = response || {};
    if (status) {
      setData(data.map((i) => new MinorDTO(i)));
    }
  };

  const changeMinorPassword = async (
    minorId: number,
    body: ChangePasswordDTO,
    closeDialog: Function
  ) => {
    const response = await ATTENDEE_APIS.changeMinorPassword(minorId, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      closeDialog();
    }
  };

  const removeFromWishlist = async (eventId: number) => {
    const response = await ATTENDEE_APIS.removeFromWishlist(eventId);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getFavoriteHosts = async (
    setData: Function,
    queryParams: any,
    previousData: UserDTO[],
    setFilters: Function
  ) => {
    const response = await ATTENDEE_APIS.getFavoriteHosts(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages } = data;
      if (setFilters) setFilters((filters) => ({ ...filters, totalPages }));
      const hosts = data.content.map((i) => new UserDTO(i));
      setData(Array.isArray(data.content) ? [...previousData, ...hosts] : []);
    }
  };

  const deleteMinor = async (minorId: number, onSuccess: Function) => {
    const response = await ATTENDEE_APIS.deleteMinor(minorId);
    const { status = false, message = "" } = response || {};
    if (status) {
      getUserProfile();
      successToaster(message);
      onSuccess();
    }
  };

  const getMinorById = async (minorId: number, setData: Function) => {
    const response = await ATTENDEE_APIS.getMinorById(minorId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(new MinorDTO(data));
    }
  };

  const favUnfavHost = async (hostId: number) => {
    const response = await ATTENDEE_APIS.favUnfavHost(hostId);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { message } = data;
      successToaster(message);
    }
  };

  const updateMinor = async (
    minorId: number,
    body: Partial<MinorDTO>,
    minorStateData: MinorDTO
  ) => {
    const response = await ATTENDEE_APIS.updateMinor(minorId, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      navigate(siteRoutes.familyMembers);
    }
  };

  const getMyBadges = async (
    setData: Function,
    queryParams: any,
    setTotalElements?: Function
  ) => {
    const response = await ATTENDEE_APIS.getMyBadges(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { badges, totalElements = 0 } = data;
      setData(badges);
      setTotalElements?.(totalElements);
    }
  };

  const uploadFile = async (file: File): Promise<PictureDTO | null> => {
    const body = new FormData();
    body.append("file", file);
    const response = await ATTENDEE_APIS.uploadFile(body);
    const { status = false, data } = response || {};
    if (status) {
      return new PictureDTO(data);
    } else {
      return null;
    }
  };

  const getCupGradituteHostById = async (id: number, setData: Function) => {
    const response = await ATTENDEE_APIS.getCupGradituteHostById(id);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const getHostReviews = async (
    setData: Function,
    queryParams: any = {},
    previousData: ReviewFilterDTO[] = [],
    setFilters?: Function
  ) => {
    const response = await ATTENDEE_APIS.getHostReviews(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages, totalElements } = data;
      if (setFilters)
        setFilters((filters) => ({ ...filters, totalPages, totalElements }));
      const events = data.content.map((i) => new ReviewFilterDTO(i));
      setData(Array.isArray(data.content) ? [...previousData, ...events] : []);
    }
  };

  const getEventsReviews = async (
    setData: Function,
    queryParams: any = {},
    previousData: ReviewFilterDTO[] = [],
    setFilters?: Function
  ) => {
    const response = await ATTENDEE_APIS.getEventsReviews(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages, totalElements } = data;
      if (setFilters)
        setFilters((filters) => ({ ...filters, totalPages, totalElements }));
      const events = data.content.map((i) => new ReviewFilterDTO(i));
      setData(Array.isArray(data.content) ? [...previousData, ...events] : []);
    }
  };

  const addToWaitlist = async (id: number) => {
    const response = await ATTENDEE_APIS.addToWaitlist(id);
    const { status = false, data = {} } = response || {};
    if (status) {
      successToaster(data);
    }
  };

  const removeFromWaitlist = async (id: number) => {
    const response = await ATTENDEE_APIS.removeFromWaitlist(id);
    const { status = false, data = {} } = response || {};
    if (status) {
      successToaster(data);
    }
  };

  const removeJoiningMember = async (eventId: number, minorId: number) => {
    const response = await ATTENDEE_APIS.removeJoiningMember(eventId, minorId);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getCommunities = async (setData: Function, queryParams: any = {}) => {
    const response = await ATTENDEE_APIS.getCommunities(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { content = [] } = data;
      setData(content.map((i) => new CommunityDTO(i)));
    }
  };

  const getCommunityMessages = async (
    queryParams: any,
    setData: Function,
    messages: MessageDTO[],
    setFilters: Function,
    preventScrolling: Function
  ) => {
    const response = await ATTENDEE_APIS.getCommunityMessages(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages } = data;
      const myUserId = getUserId();
      const { content = [] } = data;
      if (setFilters)
        setFilters((filters) => new FilterDTO({ ...filters, totalPages }));
      const dataToSet = content.map(
        (i) => new MessageDTO({ ...i, sentByMe: i.senderId === myUserId })
      );
      setData([...dataToSet.reverse(), ...messages]);
      // else {
      //     preventScrolling();
      // }
    }
  };

  const sendMessageToCommunity = async (
    message: MessageDTO,
    messages: MessageDTO[],
    setMessages: Function
  ) => {
    const response = await ATTENDEE_APIS.sendMessageToCommunity(message);
    const { status = false, data = {} } = response || {};
    if (status) {
      // Update the last message with server response data using functional update
      setMessages((prevMessages: MessageDTO[]) => {
        const updatedMessages = [...prevMessages];
        const lastIndex = updatedMessages.length - 1;
        if (lastIndex >= 0) {
          updatedMessages[lastIndex] = new MessageDTO({
            ...data,
            sentByMe: true,
          });
        }
        return updatedMessages;
      });
    }
  };

  const getLearningStyleQuestions = async (setLearningStyleData: Function) => {
    const response = await ATTENDEE_APIS.getLearningStyleQuestions();
    const { status = false, data = {} } = response || {};
    if (status) {
      setLearningStyleData(data);
    }
  };

  const getLearningSkillsQuestions = async (
    setLearningSkillData: Function,
    queryParams: any
  ) => {
    const response = await ATTENDEE_APIS.getLearningSkillsQuestions(
      queryParams
    );
    const { status = false, data = {} } = response || {};
    if (status) {
      setLearningSkillData(data);
    }
  };

  // const postLearningStyleAnswers = async (body: any) => {
  //   const response = await ATTENDEE_APIS.postLearningStyleAnswers(body);
  //   const { status = false, message = "" } = response || {};

  //   if (status) {
  //     successToaster(message);
  //     const savedMinor = localStorage.getItem("minorProfile");
  //     if (savedMinor) {
  //       const parsedMinor = new MinorDTO(JSON.parse(savedMinor));
  //       getMinorById(parsedMinor?.id, setMinor);
  //       setMinor(parsedMinor);
  //       if (parsedMinor.isLearningAssessment === true) {
  //         localStorage.removeItem("learningType");
  //         navigate(
  //           `/attendee/family-detail?id=${parsedMinor.id}&tab=Assessment`
  //         );
  //       } else {
  //         navigate(siteRoutes.assessmentLearningSkills);
  //       }
  //     }
  //   }
  // };

  const getMinorByIdForLearning = async (
    id: number
  ): Promise<MinorDTO | null> => {
    try {
      const response = await ATTENDEE_APIS.getMinorById(id);
      if (response?.status) {
        const minor = new MinorDTO(response.data);
        return minor;
      }
      return null;
    } catch (err) {
      console.error("Error fetching minor", err);
      return null;
    }
  };

  const postLearningStyleAnswers = async (body: any, setMinor: Function) => {
    const response = await ATTENDEE_APIS.postLearningStyleAnswers(body);
    const { status = false, message = "" } = response || {};

    if (status) {
      successToaster(message);

      const savedMinor = localStorage.getItem("minorProfile");
      if (savedMinor) {
        const parsedMinor = new MinorDTO(JSON.parse(savedMinor));

        const freshMinor = await getMinorByIdForLearning(parsedMinor?.id);

        if (freshMinor?.isLearningAssessment) {
          setMinor(freshMinor);
          localStorage.setItem("minorProfile", JSON.stringify(freshMinor));
          if (
            freshMinor.isLearningAssessment === true &&
            freshMinor?.isCoreConceptAssessment === true
          ) {
            navigate(
              `/attendee/family-detail?id=${freshMinor.id}&tab=Assessment`
            );
          } else {
            navigate(siteRoutes.assessmentLearningSkills);
          }
        }
      }
    }
    return response;
  };

  const getAssessmentSummaryById = async (
    childId: number,
    setAssessmentSummaryData: Function
  ) => {
    const response = await ATTENDEE_APIS.getAssessmentSummaryById(childId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setAssessmentSummaryData(data);
    }
  };

  const getAssessmentCoreConceptsById = async (
    childId: number,
    setAssessmentCoreConceptsData: Function
  ) => {
    const response = await ATTENDEE_APIS.getAssessmentCoreConceptsById(childId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setAssessmentCoreConceptsData(data);
    }
  };

  const addCOGReviewToEvent = async (hostId: number, body: any) => {
    const response = await ATTENDEE_APIS.addCOGReviewToEvent(hostId, body);
    const { status = false, message = "", data = {} } = response || {};
    if (status) {
      successToaster(message);
      const { checkoutUrl, requiresPayment } = data as {
        checkoutUrl?: string;
        requiresPayment?: boolean;
      };
      if (requiresPayment && checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    }
  };

  const getAllCOG = async (setData: Function, queryParams: any = {}) => {
    const response = await ATTENDEE_APIS.getAllCOG(queryParams);
    console.log(response);
    const { status = false, data = null } = response || {};
    if (status) {
      const { content = [] } = data || {};
      setData(content);
    }
  };

  const toggleNotification = async () => {
    const response = await ATTENDEE_APIS.toggleNotification();
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getAttendeeProfile = async (setMinorProfileData: Function) => {
    const response = await ATTENDEE_APIS.getAttendeeProfile();
    const { status = false, data } = response || {};
    if (status) {
      setMinorProfileData({
        ...data.user,
        ...data.profiles.attendee,
      });
    }
  };

  const postAttendeeDeclaration = async (body: any) => {
    const response = await ATTENDEE_APIS.postAttendeeDeclaration(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      getUserProfile();
      successToaster(message);
      updateProfileCompletionStatus("attendee");
      navigate(siteRoutes.attendeeHome);
    }
  };

  return {
    updateProfile,
    getAttendeeMinors,
    addMinorToProfile,
    getAttendeeHome,
    addMinorToEvent,
    getUserById,
    removeFromWishlist,
    addToWishlist,
    getWishlistedMinors,
    deleteMinor,
    getMinorById,
    changeMinorPassword,
    updateMinor,
    getFavoriteHosts,
    favUnfavHost,
    getMyBadges,
    uploadFile,
    getCupGradituteHostById,
    getHostReviews,
    addToWaitlist,
    removeJoiningMember,
    removeFromWaitlist,
    getEventsReviews,
    getCommunities,
    getCommunityMessages,
    sendMessageToCommunity,
    getLearningStyleQuestions,
    postLearningStyleAnswers,
    getLearningSkillsQuestions,
    getAssessmentSummaryById,
    getAssessmentCoreConceptsById,
    addCOGReviewToEvent,
    getAllCOG,
    getAttendeeProfile,
    toggleNotification,
    postAttendeeDeclaration,
    EditProfileAttendee,
  };
};

export default useAttendee;
